import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import type { RichContext } from "@/hooks/useRichAniContext";
import { refreshInAppAgentContextForSend } from "@/services/inAppAgent/buildInAppAgentRuntimeContext";
import { buildFeAppStateForSend } from "./feAppState";

const APP_STATE_KEYS = new Set([
  "focusedCaseId",
  "focusedClientId",
  "focusedArtifactId",
  "caseId",
  "clientId",
  "artifactId",
  "missionId",
  "taskId",
  "meetingId",
  "portfolio_id",
  "portfolioId",
  "portfolio_name",
  "portfolioName",
  "as_at",
  "portfolio_as_at",
]);

const PLATFORM_AGENT_CONTEXT_KEYS = new Set([
  "company_id",
  "standup",
  "review_queue",
  "review_queue_count",
  "next_meeting",
  "user_surface",
  "priority_task_id",
  "guided_flow",
]);

const MAX_OS_WINDOWS = 12;

function trimAppState(appState: unknown): Record<string, unknown> {
  if (!appState || typeof appState !== "object" || Array.isArray(appState)) {
    return {};
  }
  const record = appState as Record<string, unknown>;
  const trimmed: Record<string, unknown> = {};
  for (const key of APP_STATE_KEYS) {
    if (key in record && record[key] != null) {
      trimmed[key] = record[key];
    }
  }
  return trimmed;
}

function trimAgentContext(agentContext: unknown): Record<string, unknown> {
  if (!agentContext || typeof agentContext !== "object" || Array.isArray(agentContext)) {
    return {};
  }
  const record = agentContext as Record<string, unknown>;
  const trimmed: Record<string, unknown> = {};
  for (const key of PLATFORM_AGENT_CONTEXT_KEYS) {
    if (key in record) {
      trimmed[key] = record[key];
    }
  }
  const reviewQueue = record.review_queue;
  if (Array.isArray(reviewQueue) && reviewQueue.length > 3) {
    trimmed.review_queue = reviewQueue.slice(0, 3);
    trimmed.review_queue_truncated = reviewQueue.length;
  }
  return trimmed;
}

function trimOsContext(osContext: unknown): Record<string, unknown> | undefined {
  if (!osContext || typeof osContext !== "object" || Array.isArray(osContext)) {
    return undefined;
  }
  const record = osContext as Record<string, unknown>;
  const workspaces = record.workspaces;
  if (!Array.isArray(workspaces)) {
    return record;
  }

  let windowCount = 0;
  const trimmedWorkspaces: Record<string, unknown>[] = [];
  for (const workspace of workspaces) {
    if (!workspace || typeof workspace !== "object") continue;
    const ws = workspace as Record<string, unknown>;
    const windows = ws.windows;
    if (!Array.isArray(windows)) {
      trimmedWorkspaces.push(ws);
      continue;
    }

    const keptWindows: Record<string, unknown>[] = [];
    for (const window of windows) {
      if (!window || typeof window !== "object") continue;
      const win = window as Record<string, unknown>;
      if (win.isMinimized) continue;
      if (windowCount >= MAX_OS_WINDOWS) break;
      keptWindows.push(win);
      windowCount += 1;
    }

    trimmedWorkspaces.push({ ...ws, windows: keptWindows });
  }

  return { ...record, workspaces: trimmedWorkspaces };
}

export function shapeFeContextPreview(feContext: Record<string, unknown>): Record<string, unknown> {
  const shaped: Record<string, unknown> = { ...feContext };
  shaped.app_state = trimAppState(feContext.app_state);
  shaped.agent_context = trimAgentContext(feContext.agent_context);
  const os = trimOsContext(feContext.os_context);
  if (os) {
    shaped.os_context = os;
  }
  return shaped;
}

function agentContextBaseFromAppState(appState: unknown): Record<string, unknown> | undefined {
  if (!appState || typeof appState !== "object" || Array.isArray(appState)) {
    return undefined;
  }
  const nested = (appState as Record<string, unknown>).in_app_agent;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return undefined;
}

export type AgentPayloadPreviewParams = {
  richContext: RichContext;
  /** When provided (e.g. dev menu from sidebar), matches send-time OS snapshot. */
  osContext?: OsAniContextSnapshot;
  currentApp?: string;
  personalityMode?: string;
  companyId?: string;
};

/** Preview of `fe_context` after worker-side trimming (next message send). */
export function buildAgentPayloadPreview(params: AgentPayloadPreviewParams): Record<string, unknown> {
  const { richContext, osContext: osOverride, currentApp, personalityMode, companyId } = params;
  const osContext = osOverride ?? richContext.osContext;
  const focusedWindow = osContext?.focusedWindow ?? null;

  const rawAppState =
    richContext.appState && typeof richContext.appState === "object" && !Array.isArray(richContext.appState)
      ? (richContext.appState as Record<string, unknown>)
      : undefined;

  const appState = buildFeAppStateForSend(rawAppState, focusedWindow);

  const agentBase =
    agentContextBaseFromAppState(richContext.appState) ??
    (companyId ? { company_id: companyId } : undefined);

  const agentContext =
    osContext && agentBase
      ? refreshInAppAgentContextForSend(agentBase, osContext as OsAniContextSnapshot)
      : agentBase;

  const feContext: Record<string, unknown> = {
    current_app: currentApp ?? osContext?.focusedApp ?? "home",
    personality_mode: personalityMode,
    os_context: osContext,
    app_state: appState,
    agent_context: agentContext,
  };

  return shapeFeContextPreview(feContext);
}
